import React, { useState, useCallback, useRef, useEffect } from "react";
import { EnhancedInput } from "../Input/EnhancedInput/EnhancedInput";
import { SpinLoading } from "antd-mobile";
import { useT } from "../../../i18n/hooks";
import { CheckShieldOutlineIcon } from "../../Icon/IconSvgMap";
import utils from "../../../utils/utils";
import "./ImageCode.scss";
export interface ImageCodeProps {
  value?: string;
  onChange?: (value: string) => void;
  error?: boolean;
  placeholder?: string;
}

export const ImageCode: React.FC<ImageCodeProps> = ({
  value,
  onChange,
  error = false,
  placeholder,
}) => {
  const t = useT();
  const [imageUrl, setImageUrl] = useState<string>(
    () => `/api/auth/image_code?t=${utils.getServerTime()}`
  );
  const [loadFail, setLoadFail] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);
  const lastLoadTimeRef = useRef<number>(utils.getServerTime());

  const reloadImage = useCallback(() => {
    // setLoaded(false);
    setLoadFail(false);
    const url = `/api/auth/image_code?t=${utils.getServerTime()}`;
    setImageUrl(url);
    lastLoadTimeRef.current = utils.getServerTime();
  }, []);

  useEffect(() => {
    setLoadFail(false);
    setImgLoaded(false);
    lastLoadTimeRef.current = utils.getServerTime();
  }, [imageUrl]);

  const handleImageLoad = () => {
    // setLoaded(true);
    setLoadFail(false);
  };

  const handleImageError = () => {
    setLoadFail(true);
  };

  const handleImageClick = () => {
    if (loadFail || utils.getServerTime() - lastLoadTimeRef.current > 1000) {
      reloadImage();
    }
  };

  const imageCodeContent = (
    <div className="image-code__image-container">
      {!loadFail && imageUrl ? (
        <>
          {!imgLoaded && (
            <div className="image-code__loading">
              <SpinLoading style={{ "--size": "16px" }} />
            </div>
          )}
          <img
            className="image-code__image"
            src={imageUrl}
            alt="image-code"
            style={{ display: imgLoaded ? "block" : "none" }}
            onLoad={() => {
              setImgLoaded(true);
              handleImageLoad();
            }}
            onError={() => {
              setImgLoaded(false);
              handleImageError();
            }}
            onClick={handleImageClick}
          />
        </>
      ) : (
        <div
          style={{ cursor: "pointer", display: "contents" }}
          onClick={() => reloadImage()}
        >
          <svg
            width="28"
            height="28"
            viewBox="0 0 28 28"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M18.7039 11.0324C18.3866 10.883 18.2419 10.6824 18.2699 10.4304C18.2979 10.1784 18.4053 9.97771 18.5919 9.82838C18.6479 9.79104 18.8299 9.64637 19.1379 9.39437C19.4459 9.14237 19.8239 8.83904 20.2719 8.48438C19.4879 7.58837 18.5546 6.88371 17.4719 6.37038C16.3893 5.85704 15.2133 5.60037 13.9439 5.60037C12.7679 5.60037 11.6666 5.81971 10.6399 6.25837C9.61328 6.69704 8.71728 7.29904 7.95195 8.06437C7.18662 8.82971 6.58462 9.72571 6.14595 10.7524C5.70728 11.779 5.48795 12.8804 5.48795 14.0564C5.48795 15.2137 5.70728 16.3057 6.14595 17.3324C6.58462 18.359 7.18662 19.255 7.95195 20.0204C8.71728 20.7857 9.61328 21.3877 10.6399 21.8264C11.6666 22.265 12.7679 22.4844 13.9439 22.4844C14.9146 22.4844 15.8339 22.3304 16.7019 22.0224C17.5699 21.7144 18.3633 21.2804 19.0819 20.7204C19.8006 20.1604 20.4166 19.5024 20.9299 18.7464C21.4433 17.9904 21.8213 17.1644 22.0639 16.2684C22.1946 15.839 22.4373 15.4844 22.7919 15.2044C23.1466 14.9244 23.5666 14.7844 24.0519 14.7844C24.6306 14.7844 25.1206 14.9897 25.5219 15.4004C25.9233 15.811 26.1239 16.2964 26.1239 16.8564C26.1239 17.1177 26.0773 17.3697 25.9839 17.6124C25.5919 18.919 25.0133 20.123 24.2479 21.2244C23.4826 22.3257 22.5726 23.273 21.5179 24.0664C20.4633 24.8597 19.2919 25.4804 18.0039 25.9284C16.7159 26.3764 15.3626 26.6004 13.9439 26.6004C12.2079 26.6004 10.5746 26.2737 9.04395 25.6204C7.51328 24.967 6.18328 24.071 5.05395 22.9324C3.92462 21.7937 3.02861 20.4637 2.36595 18.9424C1.70328 17.421 1.37195 15.7924 1.37195 14.0564C1.37195 12.3204 1.70328 10.687 2.36595 9.15637C3.02861 7.62571 3.92462 6.29571 5.05395 5.16637C6.18328 4.03704 7.51328 3.14104 9.04395 2.47838C10.5746 1.81571 12.2079 1.48437 13.9439 1.48438C15.8666 1.48438 17.6493 1.88104 19.2919 2.67438C20.9346 3.46771 22.3346 4.54571 23.4919 5.90837C23.9213 5.59104 24.2806 5.31571 24.5699 5.08237C24.8593 4.84904 25.0413 4.70437 25.1159 4.64837C25.2279 4.55504 25.3493 4.48504 25.4799 4.43837C25.6106 4.39171 25.7319 4.39171 25.8439 4.43837C25.9559 4.48504 26.0539 4.58304 26.1379 4.73237C26.2219 4.88171 26.2826 5.10571 26.3199 5.40438C26.3386 5.57238 26.3573 5.85704 26.3759 6.25837C26.3946 6.65971 26.4133 7.12637 26.4319 7.65837C26.4506 8.19037 26.4599 8.74104 26.4599 9.31038C26.4599 9.87971 26.4506 10.4164 26.4319 10.9204C26.4133 11.555 26.2639 12.0124 25.9839 12.2924C25.8346 12.4417 25.6106 12.5257 25.3119 12.5444C25.0133 12.563 24.6959 12.535 24.3599 12.4604C23.8559 12.3484 23.3006 12.227 22.6939 12.0964C22.0873 11.9657 21.5086 11.8304 20.9579 11.6904C20.4073 11.5504 19.9219 11.4197 19.5019 11.2984C19.0819 11.177 18.8159 11.0884 18.7039 11.0324Z"
              fill="white"
            />
          </svg>
        </div>
      )}
    </div>
  );

  return (
    <div className="image-code">
      <EnhancedInput
        placeholder={placeholder ?? t("auth.imageCode")}
        value={value}
        onChange={value => onChange?.(value)}
        prefixIcon={{
          icon: <CheckShieldOutlineIcon />,
        }}
        suffixIcon={imageCodeContent}
        error={error}
      />
    </div>
  );
};
